'use client';

import { createContext, type ReactNode, useContext, useMemo } from 'react';

type RequiredFieldsContextValue = {
  isFieldRequired: (fieldPath?: string | null) => boolean;
};

const RequiredFieldsContext = createContext<RequiredFieldsContextValue>({
  isFieldRequired: () => false,
});

const normalizeFieldPath = (fieldPath?: string | null): string | null => {
  if (!fieldPath) {
    return null;
  }

  return fieldPath.replace(/\[(\d+)\]/g, '.$1');
};

const withoutArrayIndices = (fieldPath: string): string =>
  fieldPath
    .split('.')
    .filter((part) => !/^\d+$/.test(part))
    .join('.');

const fieldMatchesRequiredPath = (fieldPath: string, requiredPath: string): boolean =>
  fieldPath === requiredPath || fieldPath.startsWith(`${requiredPath}.`);

type RequiredFieldsProviderProps = {
  fieldPaths?: string[];
  children: ReactNode;
};

export const RequiredFieldsProvider = ({ fieldPaths = [], children }: RequiredFieldsProviderProps) => {
  const requiredPaths = useMemo(
    () =>
      fieldPaths
        .map((fieldPath) => normalizeFieldPath(fieldPath))
        .filter((fieldPath): fieldPath is string => fieldPath != null),
    [fieldPaths]
  );

  const value = useMemo<RequiredFieldsContextValue>(
    () => ({
      isFieldRequired: (fieldPath) => {
        const normalizedFieldPath = normalizeFieldPath(fieldPath);

        if (!normalizedFieldPath) {
          return false;
        }

        const fieldPathWithoutIndices = withoutArrayIndices(normalizedFieldPath);

        return requiredPaths.some(
          (requiredPath) =>
            fieldMatchesRequiredPath(normalizedFieldPath, requiredPath) ||
            fieldMatchesRequiredPath(fieldPathWithoutIndices, requiredPath)
        );
      },
    }),
    [requiredPaths]
  );

  return <RequiredFieldsContext.Provider value={value}>{children}</RequiredFieldsContext.Provider>;
};

export const useRequiredField = (fieldPath?: string | null, explicitRequired?: boolean): boolean => {
  const { isFieldRequired } = useContext(RequiredFieldsContext);

  return explicitRequired ?? isFieldRequired(fieldPath);
};
